/**
 * Survival's wave table and its run-scoped upgrade cards.
 *
 * Data only — nothing here imports `World`, so the table can be read, counted
 * and checked headlessly without standing a game up.
 *
 * A WAVE IS A TIDE, NOT A PULSE. The first version of this file listed exact
 * spawns at exact times: eight swarmers at t=0, four seekers at t=5, done. That
 * produces a skirmish. You clear it, you wait twelve seconds, you clear the
 * next one — and the pacing is set by how fast the table hands things over
 * rather than by how much pressure you can actually survive.
 *
 * So a wave declares a *budget* and a *front*: how many bodies it will send in
 * total, and how many it keeps alive and coming at any moment. The spawner tops
 * the front up continuously from the eastern edge for as long as the budget
 * lasts, so the horde is replaced as fast as you kill it and the wave ends when
 * you have out-killed its supply rather than when a script runs out of lines.
 * Kill faster and it arrives faster.
 *
 * NOTHING IN THE MIX SITS STILL. Turrets and mines are gone from Survival
 * entirely — they are the campaign's furniture, they are bolted to rock, and a
 * mode about a tide coming at you has no use for something that waits. Every
 * species below closes: swarmers flock in, seekers run straight, drones work in
 * formation, sentinels grind forward, hunters flank, lurkers rise off the floor.
 * All of them spawn with `march` set, which holds their perception gate open so
 * they advance from the far edge instead of loitering until the lamp finds them.
 *
 * Two budgets still shape every number, and both are measured:
 *
 * **Instance slots.** Measured in a browser: 211 mechanicals cost 83k extra
 * triangles and 0.21 ms of update, so the constraint is renderer capacity, not
 * frame time — and `queue` drops silently past capacity, which would draw fewer
 * enemies than are actually hitting you. The capacities in `world.ts` were
 * raised to suit these caps rather than these caps trimmed to suit them.
 *
 * **Lights.** Enemy lights are budgeted to the nearest 12 now (see
 * `World.budgetActorLights`), so a bigger horde no longer costs more light
 * work. That budget is what makes this size affordable at all.
 */

import type { EnemyType, PickupType } from './config';
import type { Modifier } from './upgrades';

/** One species' share of a wave's front. */
export interface WaveMix {
  type: EnemyType;
  /** Relative share of the budget. Not a count. */
  weight: number;
  /**
   * Where this species enters, as metres relative to the PLAYER's depth — not
   * as a height above the floor.
   *
   * Measured from the floor, a front of 72 filled the water column from seabed
   * to surface: an impassable curtain rather than a mass coming at you, with no
   * way over it and no way under. As an offset from the player it becomes a
   * band about 27 m tall in a 60 m column, so the tide still arrives in your
   * face while the water above and below it stays open. Climbing out of the
   * band is a real move, and only a temporary one — what is already in the water
   * stays where it is, but the next arrivals come in at your new depth.
   *
   * Negative is below you. Lurkers rise off the floor, hunters come over the
   * top, and the mass sits level with the boat.
   */
  band: [number, number];
  /** Extra hit points on top of the wave's own multiplier. */
  tough?: number;
}

/**
 * Something worth killing first: many times its species' hit points, a number
 * over the hull so the choice to focus it is informed, and it drops what it is
 * carrying when it dies.
 */
export interface WaveCarrier {
  type: EnemyType;
  drop: PickupType;
  /** Hit-point multiplier. A sentinel at 5 is 1,050 — a real decision. */
  tough: number;
  count: number;
}

export interface Wave {
  name: string;
  /** Total bodies the wave will send before it is spent. */
  budget: number;
  /** How many it keeps alive and closing at once. */
  front: number;
  mix: WaveMix[];
  /** Hit-point and score multiplier across the whole wave. */
  tough?: number;
  carriers?: WaveCarrier[];
  /** True when this wave is a milestone the HUD should announce. */
  boss?: boolean;
  /** Wake the octopus from the arena lair alongside the tide. */
  octopus?: boolean;
}

/**
 * Concurrency ceilings per species, all under half the registered capacity in
 * `world.ts`, so two overlapping species can never make `queue` drop one.
 */
export const ALIVE_CAP: Partial<Record<EnemyType, number>> = {
  // --- the mass: things that must reach you to hurt you -------------------
  //
  // Split by CONTACT versus RANGED, which is the line that actually matters.
  // Seekers were capped with the shooters because they look like ordnance, but
  // a seeker carries no gun at all — it closes and detonates. That makes it the
  // ideal horde body, and better than a swarmer at the job: a small silver fish
  // is indistinguishable from the scenery, and the report was exactly that —
  // "hard to even tell that they are enemies". A seeker is unmistakably a
  // machine that wants to reach you.
  seeker: 60, swarmer: 55, lurker: 3,
  // --- the shooters: the difficulty dial, and it stays small --------------
  drone: 14, sentinel: 4, hunter: 3,
};

/** Total live enemies, whatever the mix. */
export const GLOBAL_CAP = 130;

/**
 * Lit weight per species. Kept for the authoring check even though the renderer
 * now budgets enemy lights directly — it is still worth knowing when a wave is
 * asking for more light than it can have.
 */
export const LIT: Partial<Record<EnemyType, number>> = {
  hunter: 2, dreadnought: 4, swarmer: 0.125,
};
export const LIT_BUDGET = 16;

// The shapes a front can take. Named, because a wave should read as something
// that is happening rather than as four numbers.
const SHOAL: WaveMix[] = [
  { type: 'seeker', weight: 9, band: [-8, 8] },
  { type: 'swarmer', weight: 6, band: [-9, 9] },
];
const PATROL: WaveMix[] = [
  { type: 'seeker', weight: 9, band: [-8, 8] },
  { type: 'swarmer', weight: 5, band: [-9, 9] },
  { type: 'drone', weight: 3, band: [-5, 9] },
];
const PACK: WaveMix[] = [
  { type: 'seeker', weight: 9, band: [-8, 8] },
  { type: 'swarmer', weight: 4, band: [-9, 9] },
  { type: 'drone', weight: 3, band: [-5, 9] },
  { type: 'hunter', weight: 1, band: [-1, 13] },
];
const ARMOUR: WaveMix[] = [
  { type: 'seeker', weight: 9, band: [-8, 8] },
  { type: 'swarmer', weight: 3, band: [-9, 9] },
  { type: 'drone', weight: 3, band: [-5, 9] },
  { type: 'sentinel', weight: 1, band: [-3, 11] },
  { type: 'hunter', weight: 1, band: [-1, 13] },
];
const DEEP: WaveMix[] = [
  { type: 'seeker', weight: 9, band: [-8, 8] },
  { type: 'swarmer', weight: 3, band: [-9, 9] },
  { type: 'lurker', weight: 1, band: [-15, -3] },
  { type: 'drone', weight: 3, band: [-5, 9] },
  { type: 'sentinel', weight: 1, band: [-3, 11] },
  { type: 'hunter', weight: 1, band: [-1, 13] },
];

export const WAVES: Wave[] = [
  // Wave 1 is a wave, not an introduction. The first four were 14-23 abreast
  // and read as "a few fish went past" — you cannot call that a wave, and it
  // sets the wrong expectation for everything after it.
  { name: 'THE SHOAL TURNS', budget: 60, front: 26, mix: SHOAL },
  { name: 'RUNNERS', budget: 76, front: 32, mix: SHOAL,
    carriers: [{ type: 'drone', drop: 'shield', tough: 9, count: 1 }] },
  { name: 'FIRST PATROL', budget: 92, front: 36, mix: PATROL,
    carriers: [{ type: 'drone', drop: 'torpedo', tough: 9, count: 1 }] },
  { name: 'PRESSURE', budget: 108, front: 40, mix: PATROL, tough: 1.05,
    carriers: [{ type: 'drone', drop: 'overdrive', tough: 10, count: 1 }] },
  { name: 'THE PACK', boss: true, budget: 124, front: 44, mix: PACK, tough: 1.1,
    carriers: [{ type: 'sentinel', drop: 'aegis', tough: 3, count: 1 }] },
  { name: 'STEEL RAIN', budget: 138, front: 48, mix: PACK, tough: 1.12,
    carriers: [{ type: 'drone', drop: 'shield', tough: 10, count: 2 }] },
  { name: 'WOLFPACK', budget: 152, front: 52, mix: PACK, tough: 1.15,
    carriers: [{ type: 'sentinel', drop: 'overdrive', tough: 3.2, count: 1 }] },
  { name: 'THE WALL', budget: 166, front: 56, mix: ARMOUR, tough: 1.18,
    carriers: [{ type: 'sentinel', drop: 'aegis', tough: 3.4, count: 2 }] },
  { name: 'GRINDER', budget: 180, front: 60, mix: ARMOUR, tough: 1.2,
    carriers: [{ type: 'sentinel', drop: 'shield', tough: 3.6, count: 2 }] },
  // The octopus reaches the whole 52 m column from a lair under the arena and
  // adds seven lights AFTER drawActors has run, so its tide is deliberately
  // thinner than the waves on either side of it.
  { name: 'THE THING BELOW', boss: true, octopus: true, budget: 130, front: 44,
    mix: SHOAL, tough: 1.15,
    carriers: [{ type: 'sentinel', drop: 'aegis', tough: 3, count: 1 }] },
  { name: 'FROM THE FLOOR', budget: 200, front: 66, mix: DEEP, tough: 1.25,
    carriers: [{ type: 'sentinel', drop: 'overdrive', tough: 3.8, count: 2 }] },
  { name: 'FULL TIDE', boss: true, budget: 225, front: 72, mix: DEEP, tough: 1.3,
    carriers: [{ type: 'sentinel', drop: 'aegis', tough: 4, count: 3 }] },
];

/**
 * Past the authored twelve it keeps climbing, but gently. The front flattens at
 * 56 while the budget does not: a late wave gets longer rather than denser, the
 * arena stays legible, and the run still ends because you tire before the table
 * does. `tough` creeps rather than doubling — the hard cap on how many things
 * can shoot at you is `ALIVE_CAP`, and that never moves.
 */
export function endlessWave(n: number): Wave {
  const over = n - WAVES.length;
  const tier = Math.floor(over / 3) + 1;
  const boss = n % 5 === 0;
  return {
    name: boss ? `FULL TIDE ${tier}` : `WAVE ${n}`,
    boss,
    octopus: n % 10 === 0,
    budget: 225 + over * 26,
    front: Math.min(90, 72 + over * 3),
    mix: DEEP,
    tough: 1.3 + over * 0.06,
    carriers: [{
      type: 'sentinel',
      drop: over % 2 ? 'overdrive' : 'aegis',
      tough: 4 + tier * 0.4,
      count: 2 + (tier >> 1),
    }],
  };
}

export function waveAt(n: number): Wave {
  return n <= WAVES.length ? WAVES[n - 1] : endlessWave(n);
}

/** Pick a species from a mix by weight. `r` is a 0..1 roll. */
export function pickMix(mix: WaveMix[], r: number): WaveMix {
  let total = 0;
  for (const m of mix) total += m.weight;
  let t = r * total;
  for (const m of mix) {
    t -= m.weight;
    if (t <= 0) return m;
  }
  return mix[mix.length - 1];
}

// ---------------------------------------------------------------------------
// Run-scoped power
//
// Every card is a `Modifier` with `source: 'run'`, granted under a stable id so
// a second rank REPLACES the first rather than stacking. There is no serialiser
// for the stack and Survival has no currency to buy one with — the absence is
// what enforces the PRD's rule that run power does not leave the run.
// ---------------------------------------------------------------------------

export interface RunCard {
  id: string;
  name: string;
  /** One line per rank, shown on the pick. */
  ranks: string[];
  /** Builds the modifier for a given rank, 1-based. */
  build(rank: number): Omit<Modifier, 'id' | 'source' | 'time'>;
}

const pick = <T>(a: T[], r: number) => a[Math.min(r, a.length) - 1];

export const RUN_CARDS: RunCard[] = [
  { id: 'run-plating', name: 'ABLATIVE PLATING',
    ranks: ['+45 HULL', '+100 HULL', '+175 HULL'],
    build: (r) => ({ add: { maxHull: pick([45, 100, 175], r) } }) },
  { id: 'run-deflector', name: 'DEFLECTOR LATTICE',
    ranks: ['+40 SHIELD', '+90 SHIELD', '+155 SHIELD'],
    build: (r) => ({ add: { maxShield: pick([40, 90, 155], r), shieldRegen: pick([4, 9, 15], r) } }) },
  { id: 'run-capacitor', name: 'FAST CAPACITOR',
    ranks: ['SHIELD RETURNS 30% SOONER', '50% SOONER', '66% SOONER'],
    build: (r) => ({ mul: { shieldDelay: pick([0.70, 0.50, 0.34], r) } }) },
  { id: 'run-drive', name: 'OVERTUNED DRIVE',
    ranks: ['+12% THRUST', '+26% THRUST', '+42% THRUST'],
    build: (r) => ({ mul: { thrustMul: pick([1.12, 1.26, 1.42], r) } }) },
  { id: 'run-nitro', name: 'DEEP NITRO TANK',
    ranks: ['+55 NITRO, BURNS SLOWER', '+120 NITRO', '+200 NITRO'],
    build: (r) => ({ add: { nitroMax: pick([55, 120, 200], r) }, mul: { nitroDrain: pick([0.80, 0.62, 0.45], r) } }) },
  { id: 'run-rate', name: 'OVERCHARGED CANNON',
    ranks: ['+19% FIRE RATE', '+43% FIRE RATE', '+72% FIRE RATE'],
    build: (r) => ({ mul: { cannonCooldown: pick([0.84, 0.70, 0.58], r) } }) },
  { id: 'run-shot', name: 'HEAVY SLUGS',
    ranks: ['+28% CANNON DAMAGE', '+60%', 'DOUBLE'],
    build: (r) => ({ mul: { cannonDamage: pick([1.28, 1.60, 2.00], r) } }) },
  { id: 'run-cells', name: 'POWER CELLS',
    ranks: ['+55 ENERGY, CHEAPER SHOTS', '+120 ENERGY', '+200 ENERGY'],
    build: (r) => ({ add: { maxEnergy: pick([55, 120, 200], r), energyRegen: pick([6, 13, 22], r) },
                     mul: { cannonEnergy: pick([0.85, 0.68, 0.50], r) } }) },
  { id: 'run-rack', name: 'EXPANDED RACK',
    ranks: ['+6 TORPEDOES', '+14 TORPEDOES', '+24 TORPEDOES'],
    build: (r) => ({ add: { torpedoMax: pick([6, 14, 24], r) } }) },
  { id: 'run-warhead', name: 'SHAPED WARHEADS',
    ranks: ['+30% TORPEDO DAMAGE', '+70%', 'DOUBLE, WIDER BLAST'],
    build: (r) => ({ mul: { torpedoDamage: pick([1.30, 1.70, 2.00], r), torpedoBlast: pick([1.1, 1.25, 1.5], r) } }) },
  { id: 'run-pulse', name: 'SONAR SHOCKWAVE',
    ranks: ['THE PING NOW WOUNDS', 'HARDER', 'AND WIDER'],
    build: (r) => ({ flag: { pulseOn: true },
                     mul: { pulseDamage: pick([1, 1.6, 2.2], r), pulseRadius: pick([1, 1.25, 1.6], r) } }) },
  { id: 'run-array', name: 'DEEP ARRAY',
    ranks: ['+40% SONAR RANGE, CHEAPER', '+90%', '+150%'],
    build: (r) => ({ mul: { sonarRange: pick([1.4, 1.9, 2.5], r), sonarCost: pick([0.8, 0.6, 0.4], r) } }) },
  { id: 'run-lamp', name: 'FLOOD ARRAY',
    ranks: ['BRIGHTER, WIDER LAMP', 'MUCH BRIGHTER', 'DAYLIGHT'],
    build: (r) => ({ mul: { lampRange: pick([1.3, 1.6, 2.0], r), lampIntensity: pick([1.3, 1.7, 2.2], r),
                            lampHalfAngle: pick([1.15, 1.3, 1.45], r) } }) },
  { id: 'run-hardened', name: 'HARDENED FRAME',
    ranks: ['IMPACTS HURT 25% LESS', '45% LESS', '65% LESS'],
    build: (r) => ({ mul: { impactMul: pick([0.75, 0.55, 0.35], r) } }) },
  { id: 'run-charges', name: 'ORDNANCE BAY',
    ranks: ['DEPTH CHARGES', '+4 CHARGES', '+8 CHARGES'],
    build: (r) => ({ flag: { chargeUnlocked: true }, add: { chargeMax: pick([4, 8, 12], r) } }) },
];
